<template>
  <transition name="unsubscribe-pay-range-dialog" appear>
    <div
      class="ufprd__overlay"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      :aria-describedby="bodyId"
      @mousedown.self="onCancel"
    >
      <div class="ufprd__card">
        <div class="ufprd__header">
          <div class="ufprd__header-top">
            <div class="ufprd__featured" aria-hidden="true">
              <span class="ufprd__featured-glyph">
                <img
                  class="ufprd__featured-icon"
                  :src="bellOffIcon"
                  alt=""
                  width="20"
                  height="20"
                />
              </span>
            </div>
            <button
              type="button"
              class="ufprd__close-btn"
              aria-label="Close"
              :disabled="submitting"
              @click="onCancel"
            >
              <Icon name="popup_close" />
            </button>
          </div>
          <div class="ufprd__copy">
            <h2 :id="titleId" class="ufprd__title">{{ title }}</h2>
            <p :id="bodyId" class="ufprd__body">{{ bodyCopy }}</p>
          </div>
        </div>

        <div class="ufprd__body-section">
          <div v-if="codeLabel || windowLabel" class="ufprd__meta">
            <span v-if="codeLabel" class="ufprd__code">{{ codeLabel }}</span>
            <span v-if="windowLabel" class="ufprd__window">{{ windowLabel }}</span>
          </div>

          <div class="ufprd__field">
            <label class="ufprd__label" id="unsubscribe-effective-to-label">Effective to</label>
            <div class="ufprd__date">
              <button
                type="button"
                class="ufprd__date-btn"
                :class="{ 'ufprd__date-btn--empty': !effectiveToLabel }"
                :disabled="submitting"
                aria-labelledby="unsubscribe-effective-to-label"
                @click="openDatePicker"
              >
                <svg
                  class="ufprd__date-btn-icon"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M15.833 3.333H4.167c-.92 0-1.667.747-1.667 1.667v11.667c0 .92.746 1.666 1.667 1.666h11.666c.92 0 1.667-.746 1.667-1.666V5c0-.92-.746-1.667-1.667-1.667ZM13.333 1.667V5M6.667 1.667V5M2.5 8.333h15"
                    stroke="currentColor"
                    stroke-width="1.67"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>{{ effectiveToLabel || "Select a date" }}</span>
              </button>
              <DatePicker
                v-if="isDatePickerOpen"
                :isDatePickerOpen="isDatePickerOpen"
                :value="effectiveTo"
                :minDate="minEffectiveToDate"
                :modal="true"
                @onApplyDate="onApplyDate"
                @onClose="closeDatePicker"
              />
            </div>
            <p class="ufprd__hint">Must be on or after {{ minEffectiveToLabel }}</p>
          </div>
        </div>

        <div class="ufprd__footer">
          <div class="ufprd__footer-action">
            <StandardButton
              type="magenta"
              variant="outline"
              size="normal"
              label="Cancel"
              :disabled="submitting"
              @click="onCancel"
            />
          </div>
          <div class="ufprd__footer-action">
            <StandardButton
              type="terminated"
              variant="primary"
              size="normal"
              :label="submitting ? 'Unsubscribing…' : 'Unsubscribe'"
              :disabled="!canConfirm || submitting"
              :loaderLeft="submitting"
              @click="onConfirm"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import bellOffIcon from "@components/assets/bell-off-02.svg";
import {
  deriveSubscriptionState,
  subscriptionSubtitle,
} from "@components/PayRangeSubscriptionCard/payRangeSubscriptionCardUtils";
import {
  UNSUBSCRIBE_BODY,
  buildUnsubscribeTitle,
  formatEffectiveToLabel,
  isEffectiveToValid,
  minEffectiveTo,
  toIsoDate,
} from "./unsubscribeFromPayRangeUtils";

const props = defineProps({
  payRangeName: {
    type: String,
    required: true,
  },
  payRangeCode: {
    type: String,
    default: "",
  },
  effectiveFrom: {
    type: String,
    required: true,
  },
  subscriptionEffectiveTo: {
    type: String,
    default: null,
  },
  effectiveTo: {
    type: String,
    default: null,
  },
  today: {
    type: [String, Date],
    default: null,
  },
  submitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["cancel", "unsubscribe", "update:effectiveTo"]);

const uid = getCurrentInstance()?.uid ?? "unsubscribe";
const titleId = `unsubscribe-pay-range-dialog-title-${uid}`;
const bodyId = `unsubscribe-pay-range-dialog-body-${uid}`;
const isDatePickerOpen = ref(false);
const bodyCopy = UNSUBSCRIBE_BODY;

const title = computed(() => buildUnsubscribeTitle(props.payRangeName));

const codeLabel = computed(() => String(props.payRangeCode || "").trim());

const windowLabel = computed(() =>
  subscriptionSubtitle(
    deriveSubscriptionState(props.effectiveFrom, props.subscriptionEffectiveTo, props.today),
    props.effectiveFrom,
    props.subscriptionEffectiveTo
  )
);

const minEffectiveToDate = computed(() => minEffectiveTo(props.effectiveFrom));

const minEffectiveToLabel = computed(() => formatEffectiveToLabel(minEffectiveToDate.value));

const effectiveToLabel = computed(() => formatEffectiveToLabel(props.effectiveTo));

const canConfirm = computed(() => isEffectiveToValid(props.effectiveTo, props.effectiveFrom));

function openDatePicker() {
  if (props.submitting) return;
  isDatePickerOpen.value = true;
}

function closeDatePicker() {
  isDatePickerOpen.value = false;
}

function onApplyDate(date) {
  const iso = toIsoDate(date);
  if (iso) emit("update:effectiveTo", iso);
  closeDatePicker();
}

function onCancel() {
  if (props.submitting) return;
  emit("cancel");
}

function onConfirm() {
  if (!canConfirm.value || props.submitting) return;
  emit("unsubscribe", { effectiveTo: props.effectiveTo });
}

function onKeydown(event) {
  if (event.key !== "Escape" || props.submitting) return;
  if (isDatePickerOpen.value) {
    closeDatePicker();
    return;
  }
  onCancel();
}

onMounted(() => {
  document.addEventListener("keydown", onKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped>
.ufprd__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.ufprd__card {
  position: relative;
  width: 400px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: #ffffff;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  font-family: Inter, sans-serif;
}

.ufprd__header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 24px 24px 0;
  box-sizing: border-box;
}

.ufprd__header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
}

.ufprd__featured {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
}

.ufprd__featured-glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  overflow: hidden;
}

.ufprd__featured-icon {
  display: block;
  width: 20px;
  height: 20px;
}

.ufprd__close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: -8px -8px 0 0;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
  box-sizing: border-box;
}

.ufprd__close-btn:hover:not(:disabled) {
  background: #f2f4f7;
  color: #667085;
}

.ufprd__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.ufprd__close-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.ufprd__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.ufprd__title {
  margin: 0;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  overflow-wrap: break-word;
}

.ufprd__body {
  margin: 0;
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.ufprd__body-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px 24px 0;
  box-sizing: border-box;
}

.ufprd__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f9fafb;
  color: #475467;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

.ufprd__code {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  color: #344054;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.ufprd__window {
  min-width: 0;
}

.ufprd__field {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
}

.ufprd__label {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.ufprd__date {
  position: relative;
  width: 100%;
}

.ufprd__date-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
}

.ufprd__date-btn:hover:not(:disabled) {
  border-color: #98a2b3;
}

.ufprd__date-btn:focus-visible {
  outline: none;
  border-color: #98a2b3;
  box-shadow:
    0 0 0 4px #f2f4f7,
    0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

.ufprd__date-btn--empty {
  color: #667085;
  font-weight: 400;
}

.ufprd__date-btn:disabled {
  background: #f9fafb;
  cursor: default;
}

.ufprd__date-btn-icon {
  flex-shrink: 0;
  color: #175cd3;
}

.ufprd__hint {
  margin: 0;
  color: #667086;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.ufprd__footer {
  display: flex;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  padding: 24px;
  box-sizing: border-box;
}

.ufprd__footer-action {
  flex: 1 1 0;
  min-width: 0;
}

.ufprd__footer-action ::v-deep(.standard-button) {
  width: 100%;
  box-sizing: border-box;
}

.unsubscribe-pay-range-dialog-enter-active,
.unsubscribe-pay-range-dialog-leave-active {
  transition: opacity 180ms ease;
}

.unsubscribe-pay-range-dialog-enter,
.unsubscribe-pay-range-dialog-leave-to {
  opacity: 0;
}

.unsubscribe-pay-range-dialog-enter-active .ufprd__card,
.unsubscribe-pay-range-dialog-leave-active .ufprd__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.unsubscribe-pay-range-dialog-enter .ufprd__card,
.unsubscribe-pay-range-dialog-leave-to .ufprd__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}
</style>
